import { Options, UuidQuery } from "@/common/api/_types";
import { isErrorCode } from "@/components/error-page/_helpers/error-code";
import { ErrorPage } from "@/components/error-page/error-page";
import { Loader, LoaderSize } from "@/components/ui/loader";
import type { UseQueryResult } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useTranslation } from "react-i18next";

interface UuidExistenceWrapperProps<TQueryParameters = Record<string, unknown>, TData = unknown> {
  uuid: string;
  children: React.ReactNode;
  query: UuidQuery<TQueryParameters, TData>;
  queryResult?: UseQueryResult<TData, Error>;
  queryParams?: Options<TQueryParameters, never>;
  showLoadingState?: boolean;
  notFoundProps?: {
    title?: string;
    message?: string;
    description?: string;
    backTo?: string;
    backToLabel?: string;
  };
}

export function UuidExistenceWrapper<TQueryParameters = Record<string, unknown>, TData = unknown>({
  uuid,
  children,
  query,
  queryResult,
  queryParams = {
    parameters: { select: ["uuid"] } as TQueryParameters,
  },
  showLoadingState = true,
  notFoundProps,
}: UuidExistenceWrapperProps<TQueryParameters, TData>) {
  const { t } = useTranslation("common");
  const queryState = queryResult ?? query(uuid, queryParams);
  const { data, error, isPending, isError } = queryState;

  if (isPending && showLoadingState) {
    return (
      <div className="flex min-h-100 items-center justify-center">
        <Loader size={LoaderSize.Medium} />
      </div>
    );
  }

  const errorStatus = isAxiosError(error) ? error.response?.status : undefined;

  if (isError && errorStatus !== 404) {
    return (
      <ErrorPage
        errorCode={isErrorCode(errorStatus) ? errorStatus : null}
        backTo={notFoundProps?.backTo || "~/"}
        backToLabel={notFoundProps?.backToLabel || t("fit:BackToOverview")}
      />
    );
  }

  if (isError || !data) {
    return (
      <ErrorPage
        title={notFoundProps?.title || "404"}
        message={notFoundProps?.message || t("ResourceNotFound")}
        description={notFoundProps?.description || t("ResourceNotFoundDescription")}
        backTo={notFoundProps?.backTo || "~/"}
        backToLabel={notFoundProps?.backToLabel || t("fit:BackToOverview")}
      />
    );
  }

  return <>{children}</>;
}
